import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  GraduationCap,
  BookOpen,
  Briefcase,
  Building2,
  PoundSterling,
  Calendar,
  Shield,
  Clock,
  Globe2,
  Users,
  Award,
  TrendingUp,
  Mail,
  MessageCircle,
  ChevronRight,
  Star,
  Sparkles,
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import { usePageMeta, useServiceSchema, useFAQSchema } from '../../hooks/useSEO';
import BreadcrumbSchema from '../../components/seo/BreadcrumbSchema';
import SmartProfileEvalCTA from './components/SmartProfileEvalCTA';

/* ────────────────────────────────────────────────────────────────── */
/* DATA                                                                */
/* ────────────────────────────────────────────────────────────────── */

const keyReasons = [
  {
    factor: 'World-Class Education',
    details: '4 universities in the global Top 10 (Oxford, Cambridge, Imperial, UCL).',
    icon: Award,
  },
  {
    factor: 'Shorter Duration',
    details: "Master's in 1 year, Bachelor's in 3 years – save up to 50% time vs the US.",
    icon: Clock,
  },
  {
    factor: 'Post-Study Work Visa',
    details: '2-year Graduate Route Visa with no job-offer requirement.',
    icon: Briefcase,
  },
  {
    factor: 'Proximity to India',
    details: '7–8 hour flight, similar time zone, large Indian diaspora.',
    icon: Globe2,
  },
  {
    factor: 'Part-Time Work',
    details: 'Work 20 hrs/week during term, full-time during vacations.',
    icon: TrendingUp,
  },
];

const topCourses = [
  {
    title: 'Business / MBA',
    salary: '£40,000 – £60,000',
    desc: '1-year MBA from London Business School, Warwick, Cranfield. London is a global finance hub.',
    icon: Briefcase,
    link: '/study-abroad/comparison/uk-vs-australia-for-mba/',
    linkLabel: 'UK vs Australia for MBA',
  },
  {
    title: 'Data Science & AI',
    salary: '£35,000 – £50,000',
    desc: 'Top programs at Imperial, UCL, Edinburgh, Manchester. Massive demand across fintech and AI startups.',
    icon: TrendingUp,
    link: '/study-abroad/careers/uk/data-science-careers/',
    linkLabel: 'Data Science Careers in UK',
  },
  {
    title: 'Healthcare & Nursing',
    salary: '£28,000 – £45,000',
    desc: 'NHS sponsorship + clear PR pathway. Highest visa approval rate for Indian nurses.',
    icon: Shield,
    link: '/study-abroad/careers/uk/nursing-careers/',
    linkLabel: 'Nursing Careers in UK',
  },
  {
    title: 'Cybersecurity',
    salary: '£40,000 – £55,000',
    desc: 'Royal Holloway, Lancaster, Warwick lead the field. UK is Europe’s #1 cyber-talent market.',
    icon: Shield,
    link: '/study-abroad/careers/uk/cybersecurity-careers/',
    linkLabel: 'Cybersecurity Careers in UK',
  },
  {
    title: 'Engineering & STEM',
    salary: '£32,000 – £48,000',
    desc: 'Cambridge, Imperial, Manchester. Renewable energy + automotive R&D hubs across the UK.',
    icon: BookOpen,
    link: '/study-abroad/careers/uk/',
    linkLabel: 'All UK Careers',
  },
  {
    title: 'Creative & Media',
    salary: '£26,000 – £42,000',
    desc: 'UAL, Goldsmiths, Bournemouth. London is the world capital for film, fashion, and advertising.',
    icon: Sparkles,
    link: '/study-abroad/uk/best-universities/',
    linkLabel: 'Top UK Universities',
  },
];

const visaSteps = [
  {
    n: 1,
    title: 'University Application',
    desc: 'Shortlist universities, prepare SOP/LOR, submit applications via UCAS (UG) or directly (PG).',
  },
  {
    n: 2,
    title: 'Offer Letter',
    desc: 'Receive Conditional or Unconditional Offer Letter from your chosen university.',
  },
  {
    n: 3,
    title: 'CAS Issuance',
    desc: 'Pay deposit, submit financial proof, receive the Confirmation of Acceptance for Studies (CAS).',
  },
  {
    n: 4,
    title: 'Form Filling',
    desc: 'Complete the UK Student Visa application online, pay £363 visa fee + £776/year IHS.',
  },
  {
    n: 5,
    title: 'Biometrics',
    desc: 'Visit your nearest VFS Global centre for fingerprints, photograph, and document submission.',
  },
  {
    n: 6,
    title: 'Visa Decision',
    desc: 'Decision typically issued within 15 working days. Receive vignette + BRP on arrival.',
  },
];

const comparisons = [
  {
    headline: 'UK vs Canada',
    insight:
      "UK offers faster 1-year master's degrees; Canada offers an easier and more predictable PR pathway with Express Entry.",
    link: '/study-abroad/comparison/canada-vs-uk-for-pr/',
    cta: 'Read full Canada vs UK PR comparison',
  },
  {
    headline: 'UK vs Australia (MBA)',
    insight:
      'UK MBAs are cost-effective (1 year, ~₹35–55 L total). Australian MBAs cost more but offer longer 4-year post-study work rights.',
    link: '/study-abroad/comparison/uk-vs-australia-for-mba/',
    cta: 'Read UK vs Australia MBA breakdown',
  },
  {
    headline: 'Ireland vs UK (Masters)',
    insight:
      'Ireland offers a 2-year Stay-Back Visa and lower tuition. UK wins on global brand and university ranking depth.',
    link: '/study-abroad/comparison/ireland-vs-uk-for-masters/',
    cta: 'Read Ireland vs UK Masters guide',
  },
  {
    headline: 'Germany vs UK (Data Science)',
    insight:
      'Germany = near-zero tuition + strong industry tie-ups. UK = faster degree, stronger English-only ecosystem, higher starting salaries.',
    link: '/study-abroad/comparison/germany-vs-uk-for-data-science/',
    cta: 'Read Germany vs UK Data Science verdict',
  },
];

const faqs = [
  {
    q: 'Is studying in the UK worth it for Indian students in 2026?',
    a: "Yes. The UK offers 1-year master's degrees, a 2-year Graduate Route post-study work visa with no job-offer requirement, and access to 4 of the world's top 10 universities. ROI is faster than US/Canada because the degree is half the duration.",
  },
  {
    q: 'How much does it cost to study in the UK for Indian students?',
    a: "Total budget for a 1-year master's degree typically ranges between ₹35–55 Lakhs. This includes tuition (₹18–35 L), living costs (₹8–15 L), visa fee (£363), and IHS (£776/year).",
  },
  {
    q: 'What is the minimum IELTS score required for UK student visa?',
    a: 'Most universities accept an overall IELTS band of 6.0–6.5 with no section below 5.5–6.0. For visa purposes, the UKVI IELTS (Secure English Language Test) is preferred. PTE Academic 58–65 is also widely accepted.',
  },
  {
    q: 'What is the UK Graduate Route (Post-Study Work) Visa?',
    a: 'The Graduate Route allows international graduates to stay in the UK for 2 years (3 years for PhD) after completing their degree, without needing a job offer. You can work, switch employers, or start a business.',
  },
  {
    q: 'Which are the main intakes for UK universities?',
    a: 'There are three intakes: September (the largest – 80% of programs available), January (mid-year intake – majority of programs), and a smaller secondary September/May intake at select universities.',
  },
  {
    q: "Can I work part-time during my master's in the UK?",
    a: 'Yes. On a Student Visa you can work up to 20 hours per week during term time and full-time during scheduled vacations. NHS-aligned placements and university internships are paid extra.',
  },
  {
    q: 'What financial proof do I need for the UK student visa?',
    a: 'You must show funds equal to your first-year tuition + 9 months of living costs (₹12–15 L for London, ₹8–10 L outside London). Funds must sit in your account for 28 consecutive days before applying.',
  },
  {
    q: "Can I bring my spouse on a UK student visa?",
    a: "Dependants are now only allowed for PhD or research-based postgraduate programs (UK rule change from Jan 2024). Standard taught master's programs no longer permit dependants.",
  },
  {
    q: 'How long does a UK student visa take to process?',
    a: 'Standard processing is 15 working days from biometrics. Priority service (5 working days, £500 extra) and Super Priority (next working day, £1,000 extra) are also available.',
  },
  {
    q: 'Do UK universities accept 3-year bachelor degrees from India?',
    a: "Yes. Most UK universities accept 3-year Indian bachelor's degrees (BA, B.Sc., B.Com., BBA, BCA) for direct entry into 1-year master's programs.",
  },
  {
    q: 'What is the difference between conditional and unconditional offer?',
    a: 'A Conditional Offer requires you to meet pending criteria (e.g., final-year results or IELTS). An Unconditional Offer means you’ve met all requirements and your seat is confirmed.',
  },
  {
    q: 'Are scholarships available for Indian students in the UK?',
    a: 'Yes. Major scholarships include Chevening (full ride), Commonwealth Scholarship, GREAT Scholarships (£10,000), and university-specific awards from Oxford, Cambridge, Warwick, Edinburgh, and Manchester.',
  },
  {
    q: 'What is CAS and why is it important?',
    a: 'CAS (Confirmation of Acceptance for Studies) is a unique reference number issued by your UK university. It is mandatory for your Student Visa application and is issued only after fee payment + financial verification.',
  },
  {
    q: 'Can I switch to a Skilled Worker visa after my Graduate Route Visa?',
    a: 'Yes. If you secure a job that meets the Skilled Worker Visa salary threshold (£38,700 for most roles in 2026), you can switch from the Graduate Route to a Skilled Worker Visa without leaving the UK.',
  },
  {
    q: 'Is the UK safe for Indian students?',
    a: 'Yes. UK cities have low violent-crime rates, free NHS healthcare via IHS, dedicated International Student Welfare offices on every campus, and a thriving 1.5M+ Indian-origin community.',
  },
];

const stats = [
  { val: '600K+', label: 'International Students' },
  { val: '35K+', label: 'Indian Students Yearly' },
  { val: '1 Year', label: "Master's Duration" },
  { val: '2 Years', label: 'Graduate Route PSW' },
];

const quickLinks = [
  { label: 'UK Student Visa', path: '/study-abroad/uk/student-visa/', icon: Shield },
  { label: 'Best UK Universities', path: '/study-abroad/uk/best-universities/', icon: Building2 },
  { label: 'Scholarships', path: '/study-abroad/uk/scholarships/', icon: GraduationCap },
  { label: 'Cost of Study', path: '/study-abroad/uk/cost-of-study/', icon: PoundSterling },
  { label: 'Careers in UK', path: '/study-abroad/careers/uk/', icon: Briefcase },
  { label: 'MBA in UK', path: '/study-abroad/uk/mba/', icon: BookOpen },
  { label: "Masters in UK", path: '/study-abroad/uk/masters/', icon: GraduationCap },
  { label: 'Healthcare Courses', path: '/study-abroad/uk/healthcare-courses/', icon: Shield },
  { label: 'UK Intakes', path: '/study-abroad/uk/intakes/', icon: Calendar },
  { label: 'Student Accommodation', path: '/study-abroad/uk/student-accommodation/', icon: Building2 },
  { label: 'Jobs After Masters', path: '/study-abroad/uk/jobs-after-masters/', icon: Briefcase },
  { label: 'Cheapest Universities', path: '/study-abroad/uk/cheapest-universities/', icon: PoundSterling },
];

/* High-quality, freely-licensed UK campus / city imagery (Unsplash CDN). */
const CAMPUS_IMG_OXFORD =
  'https://images.unsplash.com/photo-1548783307-f63adc3f200b?auto=format&fit=crop&w=1600&q=70';
const CAMPUS_IMG_LONDON =
  'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?auto=format&fit=crop&w=1600&q=70';
const CAMPUS_IMG_CAMBRIDGE =
  'https://images.unsplash.com/photo-1564507592333-c60657eea523?auto=format&fit=crop&w=1600&q=70';
const CAMPUS_IMG_LIBRARY =
  'https://images.unsplash.com/photo-1605007493699-af65834f8a00?auto=format&fit=crop&w=1600&q=70';

/* ────────────────────────────────────────────────────────────────── */
/* COMPONENT                                                           */
/* ────────────────────────────────────────────────────────────────── */

const UKHubPage = () => {
  const [whatsAppHover, setWhatsAppHover] = useState(false);

  usePageMeta(
    'Study in UK for Indian Students 2026: Guide, Cost, Visa & Universities',
    "Complete 2026 guide to study in the UK for Indian students. 1-year master's, 2-year Graduate Route Visa, cost ₹35–55 L, IELTS 6.0–6.5, top universities, scholarships, and step-by-step visa process. Free counselling with Zafco.",
  );

  useServiceSchema({
    name: 'UK Study Abroad Consulting for Indian Students',
    description:
      "End-to-end UK study-abroad consulting: university shortlisting, SOP/LOR, CAS, visa filing, scholarship guidance and pre-departure for 1-year master's and Graduate Route visa applicants.",
    serviceType: 'Education Consulting',
    areaServed: ['United Kingdom', 'India'],
    audience: 'Indian students pursuing higher education in the UK',
    url: 'https://zafco.in/study-abroad/uk/',
  });

  useFAQSchema(faqs);

  return (
    <div className="bg-white" data-testid="sa-uk-hub-page">
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: 'https://zafco.in/' },
          { name: 'Study Abroad', url: 'https://zafco.in/study-abroad/' },
          { name: 'Study in UK', url: 'https://zafco.in/study-abroad/uk/' },
        ]}
      />

      {/* ============================ HERO ============================ */}
      <section
        className="relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #001F5C 0%, #003087 55%, #0A2A6E 100%)' }}
        data-testid="uk-hero"
      >
        {/* Background image overlay */}
        <div className="absolute inset-0 opacity-[0.18]">
          <img
            src={CAMPUS_IMG_OXFORD}
            alt="Oxford University historic campus quad in sunlight"
            loading="eager"
            width={1600}
            height={900}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#001F5C]/95 via-[#001F5C]/85 to-transparent" />
        <div className="absolute top-10 right-[-80px] w-[420px] h-[420px] bg-[#C5A028]/25 rounded-full blur-[120px] pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-28">
          <nav aria-label="Breadcrumb" className="mb-6 text-xs text-slate-300/90">
            <Link to="/" className="hover:text-white">Home</Link>
            <span className="mx-2 text-slate-500">/</span>
            <Link to="/study-abroad/" className="hover:text-white">Study Abroad</Link>
            <span className="mx-2 text-slate-500">/</span>
            <span className="text-[#C5A028] font-semibold">United Kingdom</span>
          </nav>

          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-6">
              <span className="text-lg" aria-hidden="true">{getFlagEmoji('GB')}</span>
              <span className="text-[11px] text-white/95 font-bold tracking-[0.18em] uppercase">
                2026 Updated Guide
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-syne text-white leading-[1.05] mb-5">
              Study in the <span className="text-[#C5A028]">UK</span> for Indian
              Students <span className="block text-2xl sm:text-3xl lg:text-4xl font-medium text-slate-200 mt-3">— 2026 Premium Guide</span>
            </h1>

            <p className="text-base lg:text-lg text-slate-200/90 leading-relaxed mb-8 max-w-2xl">
              Master’s in 1 year. 2-year Graduate Route Visa. 4 universities in the global Top 10.
              Get the complete admission, visa, cost & career roadmap — built by Zafco’s certified
              UK counsellors.
            </p>

            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
              <Link
                to="/study-abroad/book-counselling/?source=uk-hub-hero"
                className="inline-flex items-center justify-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5 hover:shadow-2xl hover:shadow-[#C5A028]/40"
                data-testid="uk-hero-counselling-cta"
              >
                Book Free Counselling
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="https://wa.me/919990533327"
                target="_blank"
                rel="noopener noreferrer"
                onMouseEnter={() => setWhatsAppHover(true)}
                onMouseLeave={() => setWhatsAppHover(false)}
                className="inline-flex items-center justify-center gap-2 border-2 border-white/40 hover:border-white text-white font-semibold text-sm px-7 py-3.5 rounded-full transition-all bg-white/5 backdrop-blur-sm"
                data-testid="uk-hero-whatsapp-cta"
              >
                <MessageCircle className={`w-4 h-4 transition-transform ${whatsAppHover ? 'scale-110' : ''}`} />
                WhatsApp: +91-9990533327
              </a>
            </div>

            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs text-slate-300/80">
              {['MEA-Approved Consultancy', '23+ Years Experience', '10,000+ Placements'].map((b) => (
                <div key={b} className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#C5A028]" />
                  <span>{b}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div className="relative bg-black/30 backdrop-blur-md border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-5 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-2xl lg:text-3xl font-bold font-syne text-[#C5A028]">{s.val}</div>
                <div className="text-[11px] text-slate-300 mt-0.5 uppercase tracking-wider">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================ WHY UK ============================ */}
      <section className="py-16 lg:py-24 bg-white" data-testid="uk-why-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-10">
            <span className="inline-block text-[11px] font-bold tracking-[0.22em] text-[#C5A028] uppercase mb-3">
              Section 01 · The Opportunity
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-syne text-[#001F5C] leading-tight mb-5">
              Why study in the United Kingdom?
            </h2>
            <p className="text-base lg:text-lg text-slate-600 leading-relaxed">
              The UK is one of the world’s most prestigious education systems. Every year{' '}
              <strong className="text-slate-900">600,000+ international students</strong> (including
              over <strong className="text-slate-900">35,000 Indians</strong>) choose the UK for its
              academic depth, 1-year Master’s advantage, and direct route to global careers via the{' '}
              <Link to="/blogs/uk-graduate-route-visa-updates" className="text-[#003087] font-semibold underline decoration-[#C5A028] underline-offset-4">
                Graduate Route Visa
              </Link>
              . Compared to{' '}
              <Link to="/study-abroad/" className="text-[#003087] font-semibold underline decoration-[#C5A028] underline-offset-4">
                other Study Abroad destinations
              </Link>
              , the UK offers the fastest ROI for working professionals.
            </p>
          </div>

          {/* Key Reasons – responsive table → cards */}
          <div className="rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-[#001F5C] text-white">
                    <th className="px-6 py-4 text-xs font-bold tracking-wider uppercase w-1/3">Factor</th>
                    <th className="px-6 py-4 text-xs font-bold tracking-wider uppercase">Why It Matters</th>
                  </tr>
                </thead>
                <tbody>
                  {keyReasons.map((r, i) => (
                    <tr
                      key={r.factor}
                      className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}
                    >
                      <td className="px-6 py-4 align-top">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-[#C5A028]/15 flex items-center justify-center shrink-0">
                            <r.icon className="w-4 h-4 text-[#C5A028]" />
                          </div>
                          <span className="font-bold text-slate-900 text-sm">{r.factor}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-700">{r.details}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile stacked cards */}
            <div className="md:hidden divide-y divide-slate-200">
              {keyReasons.map((r) => (
                <div key={r.factor} className="p-5 bg-white">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 rounded-lg bg-[#C5A028]/15 flex items-center justify-center">
                      <r.icon className="w-4 h-4 text-[#C5A028]" />
                    </div>
                    <h3 className="font-bold text-slate-900 text-sm">{r.factor}</h3>
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed pl-12">{r.details}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Image break */}
      <div className="relative h-64 md:h-80 overflow-hidden">
        <img
          src={CAMPUS_IMG_LONDON}
          alt="London skyline with iconic British Parliament and Big Ben tower"
          loading="lazy"
          width={1600}
          height={600}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#001F5C]/70 to-transparent flex items-center">
          <div className="max-w-7xl mx-auto px-6 w-full">
            <p className="text-white/95 text-xl md:text-3xl font-bold font-syne max-w-md leading-snug">
              From London to Edinburgh — your global career starts here.
            </p>
          </div>
        </div>
      </div>

      {/* ============== EDUCATION SYSTEM & GRADING ============== */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="uk-education-system">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-10">
            <span className="inline-block text-[11px] font-bold tracking-[0.22em] text-[#C5A028] uppercase mb-3">
              Section 02 · Education System
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#001F5C] leading-tight mb-3">
              UK Education System &amp; Grading Explained
            </h2>
            <p className="text-base text-slate-600 leading-relaxed">
              Understand course levels, durations, and how UK grades map to Indian percentages /
              CGPA — so you know exactly where you stand.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Levels of Study */}
            <div className="bg-white rounded-2xl border border-slate-200 p-7 shadow-sm">
              <h3 className="text-lg font-bold font-syne text-slate-900 mb-5 flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-[#003087]" />
                Levels of Study
              </h3>

              <ul className="space-y-4">
                <li className="border-l-4 border-[#003087] pl-4">
                  <div className="font-bold text-slate-900">Undergraduate (Bachelor’s)</div>
                  <p className="text-sm text-slate-600 mt-1">3 years · Eligibility: 12th pass 60–70%</p>
                </li>
                <li className="border-l-4 border-[#C5A028] pl-4 bg-[#C5A028]/5 py-3 -mx-1 px-4 rounded-r-lg">
                  <div className="font-bold text-slate-900 flex items-center gap-2">
                    Postgraduate (Master’s)
                    <span className="text-[10px] uppercase bg-[#C5A028] text-black font-bold px-2 py-0.5 rounded-full">Most Popular</span>
                  </div>
                  <p className="text-sm text-slate-700 mt-1">
                    1 year · Bachelor’s 55–65% required.{' '}
                    <strong className="text-[#001F5C]">Saves time &amp; ~₹15 L compared to a 2-year US Master’s.</strong>
                  </p>
                </li>
                <li className="border-l-4 border-slate-300 pl-4">
                  <div className="font-bold text-slate-900">PhD / Research</div>
                  <p className="text-sm text-slate-600 mt-1">3–4 years · Master’s with research experience</p>
                </li>
              </ul>
            </div>

            {/* Grading */}
            <div className="bg-white rounded-2xl border border-slate-200 p-7 shadow-sm">
              <h3 className="text-lg font-bold font-syne text-slate-900 mb-5 flex items-center gap-2">
                <Award className="w-5 h-5 text-[#003087]" />
                UK Grading vs Indian System
              </h3>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-slate-200">
                      <th className="text-left py-2 font-bold text-slate-900">UK Classification</th>
                      <th className="text-left py-2 font-bold text-slate-900">Percentage</th>
                      <th className="text-left py-2 font-bold text-slate-900">CGPA</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-700">
                    <tr className="border-b border-slate-100">
                      <td className="py-3 font-semibold">1st Class</td>
                      <td className="py-3">70%+</td>
                      <td className="py-3">8.5+</td>
                    </tr>
                    <tr className="border-b border-slate-100 bg-[#C5A028]/5">
                      <td className="py-3 font-semibold">2:1 (Upper Second)</td>
                      <td className="py-3">60–69%</td>
                      <td className="py-3">7.5–8.4</td>
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="py-3 font-semibold">2:2 (Lower Second)</td>
                      <td className="py-3">50–59%</td>
                      <td className="py-3">6.5–7.4</td>
                    </tr>
                    <tr>
                      <td className="py-3 font-semibold">3rd Class</td>
                      <td className="py-3">40–49%</td>
                      <td className="py-3">5.5–6.4</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-slate-500 mt-4">
                Most postgraduate UK programs require a <strong>2:1 equivalent</strong> for direct admission.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============== ELIGIBILITY & EXAMS ============== */}
      <section className="py-16 lg:py-24 bg-white" data-testid="uk-eligibility">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-10">
            <span className="inline-block text-[11px] font-bold tracking-[0.22em] text-[#C5A028] uppercase mb-3">
              Section 03 · Eligibility
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#001F5C] leading-tight mb-3">
              Eligibility &amp; English Tests
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                title: 'IELTS',
                main: '6.0 – 6.5',
                note: 'UKVI IELTS preferred for visa filing. No section below 5.5–6.0.',
                icon: BookOpen,
              },
              {
                title: 'PTE Academic',
                main: '58 – 65',
                note: 'Accepted by 99% UK universities. Fastest results (2 days).',
                icon: BookOpen,
              },
              {
                title: 'TOEFL iBT',
                main: '87 – 100',
                note: 'Accepted but UKVI-IELTS is the safer route for visa.',
                icon: BookOpen,
              },
            ].map((e) => (
              <div key={e.title} className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
                <e.icon className="w-6 h-6 text-[#003087] mb-3" />
                <div className="text-sm font-bold text-slate-900">{e.title}</div>
                <div className="text-3xl font-bold font-syne text-[#001F5C] my-2">{e.main}</div>
                <p className="text-sm text-slate-600">{e.note}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 bg-[#001F5C] text-white rounded-2xl p-7 lg:p-9 grid md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <h3 className="text-xl font-bold font-syne mb-2">UK Intakes for Indian Students</h3>
              <p className="text-sm text-slate-300 leading-relaxed">
                The UK offers three intakes annually. Choosing the right intake impacts scholarship
                availability, programme choice, and visa timelines.
              </p>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-3">
                <Calendar className="w-4 h-4 text-[#C5A028] mt-0.5 shrink-0" />
                <div><strong className="text-[#C5A028]">September (Main):</strong> Largest intake, 80% of programs.</div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar className="w-4 h-4 text-[#C5A028] mt-0.5 shrink-0" />
                <div><strong className="text-[#C5A028]">January:</strong> Mid-year intake, majority of universities.</div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar className="w-4 h-4 text-[#C5A028] mt-0.5 shrink-0" />
                <div><strong className="text-[#C5A028]">May/Secondary:</strong> Select universities only.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== TOP COURSES & SALARIES ============== */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="uk-courses">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-10">
            <span className="inline-block text-[11px] font-bold tracking-[0.22em] text-[#C5A028] uppercase mb-3">
              Section 04 · Career ROI
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#001F5C] leading-tight mb-3">
              Top Courses &amp; UK Starting Salaries (2026)
            </h2>
            <p className="text-base text-slate-600 leading-relaxed">
              Salaries are graduate-level starting compensation in the UK as of 2026. Senior roles
              after 3–5 years typically command{' '}
              <strong className="text-slate-900">1.5–2× these figures</strong>.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {topCourses.map((c) => (
              <div
                key={c.title}
                className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#C5A028] hover:shadow-lg transition-all group"
              >
                <div className="w-11 h-11 rounded-xl bg-[#003087]/8 flex items-center justify-center mb-4 group-hover:bg-[#003087] transition-colors">
                  <c.icon className="w-5 h-5 text-[#003087] group-hover:text-[#C5A028] transition-colors" />
                </div>
                <h3 className="text-lg font-bold font-syne text-slate-900 mb-1">{c.title}</h3>
                <div className="text-[#C5A028] font-bold text-sm mb-3 flex items-center gap-1">
                  <PoundSterling className="w-3.5 h-3.5" />
                  {c.salary} / year
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-4">{c.desc}</p>
                <Link
                  to={c.link}
                  className="inline-flex items-center gap-1.5 text-[#003087] font-semibold text-xs hover:gap-2 transition-all"
                >
                  {c.linkLabel} <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Image break – Cambridge */}
      <div className="relative h-56 md:h-72 overflow-hidden">
        <img
          src={CAMPUS_IMG_CAMBRIDGE}
          alt="University of Cambridge punting on the River Cam"
          loading="lazy"
          width={1600}
          height={500}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#001F5C]/85 via-transparent to-transparent" />
      </div>

      {/* ============== COST OF STUDYING ============== */}
      <section className="py-16 lg:py-24 bg-white" data-testid="uk-cost">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-10">
            <span className="inline-block text-[11px] font-bold tracking-[0.22em] text-[#C5A028] uppercase mb-3">
              Section 05 · Investment
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#001F5C] leading-tight mb-3">
              Cost of Studying in the UK (2026)
            </h2>
            <p className="text-base text-slate-600 leading-relaxed">
              Plan your finances upfront. The UK requires{' '}
              <strong className="text-slate-900">financial proof held for 28 consecutive days</strong>{' '}
              before your visa application.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
            {[
              { label: 'Annual Tuition (Master’s)', value: '₹18 – 35 L', icon: GraduationCap },
              { label: 'Living: London', value: '₹12 – 15 L / yr', icon: Building2 },
              { label: 'Living: Outside London', value: '₹8 – 10 L / yr', icon: Building2 },
              { label: 'Visa + IHS', value: '£363 + £776/yr', icon: Shield },
            ].map((c) => (
              <div key={c.label} className="bg-slate-50 border border-slate-200 rounded-2xl p-6 text-center">
                <c.icon className="w-6 h-6 text-[#C5A028] mx-auto mb-2" />
                <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">{c.label}</div>
                <div className="text-xl font-bold font-syne text-[#001F5C]">{c.value}</div>
              </div>
            ))}
          </div>

          <div
            className="rounded-2xl p-7 lg:p-9 text-white relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #001F5C 0%, #003087 100%)' }}
          >
            <div className="absolute top-0 right-0 w-72 h-72 bg-[#C5A028]/20 rounded-full blur-[100px]" />
            <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
              <div>
                <div className="text-[10px] font-bold tracking-[0.22em] text-[#C5A028] uppercase mb-2">
                  Estimated Total Budget
                </div>
                <div className="text-3xl lg:text-5xl font-bold font-syne mb-2">₹35 – 55 Lakhs</div>
                <p className="text-sm text-slate-300 max-w-lg">
                  For a complete 1-year Master’s programme — including tuition, living costs, visa,
                  IHS, and a small buffer for travel.
                </p>
              </div>
              <Link
                to="/study-abroad/uk/scholarships/"
                className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-6 py-3 rounded-full transition-all"
              >
                Explore UK Scholarships <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============== VISA PROCESS – STEPPER ============== */}
      <section className="py-16 lg:py-24 bg-[#001F5C] text-white relative overflow-hidden" data-testid="uk-visa-process">
        <div className="absolute top-20 left-[-100px] w-96 h-96 bg-[#C5A028]/15 rounded-full blur-[140px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="max-w-3xl mb-12">
            <span className="inline-block text-[11px] font-bold tracking-[0.22em] text-[#C5A028] uppercase mb-3">
              Section 06 · Visa Process
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold font-syne leading-tight mb-3">
              UK Student Visa Process — 6 Simple Steps
            </h2>
            <p className="text-base text-slate-300 leading-relaxed">
              From university application to landing in London — here’s the exact roadmap. For
              latest rule changes refer to our{' '}
              <Link to="/blogs/latest-uk-student-visa-rules" className="text-[#C5A028] underline underline-offset-4 hover:text-[#EAB308]">
                Latest UK Student Visa Rules
              </Link>{' '}
              update.
            </p>
          </div>

          <ol className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {visaSteps.map((step) => (
              <li key={step.n} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-[#C5A028]/60 transition-colors relative">
                <div className="absolute -top-4 -left-2 w-12 h-12 rounded-full bg-[#C5A028] text-black font-bold text-lg flex items-center justify-center font-syne shadow-lg">
                  {step.n}
                </div>
                <div className="pl-8">
                  <h3 className="text-base font-bold font-syne text-white mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-300 leading-relaxed">{step.desc}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-12 flex justify-center">
            <Link
              to="/study-abroad/uk/student-visa/"
              className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-7 py-3.5 rounded-full transition-all"
            >
              Full UK Student Visa Guide <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============== QUICK NAV / RESOURCE LINKS ============== */}
      <section className="py-16 bg-white" data-testid="uk-quick-links">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-10">
            <span className="inline-block text-[11px] font-bold tracking-[0.22em] text-[#C5A028] uppercase mb-3">
              Section 07 · Resource Hub
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#001F5C] leading-tight">
              Deep-Dive into Every UK Topic
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {quickLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="bg-white rounded-xl p-5 flex items-start gap-4 group border border-slate-200 hover:border-[#003087] hover:shadow-md transition-all"
              >
                <div className="w-10 h-10 rounded-lg bg-[#003087]/8 flex items-center justify-center shrink-0 group-hover:bg-[#003087] transition-colors">
                  <link.icon className="w-5 h-5 text-[#003087] group-hover:text-[#C5A028] transition-colors" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-bold text-slate-900 group-hover:text-[#003087] transition-colors">
                    {link.label}
                  </h3>
                  <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-[#003087] mt-1 transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============== COMPARISONS ============== */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="uk-comparisons">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-10">
            <span className="inline-block text-[11px] font-bold tracking-[0.22em] text-[#C5A028] uppercase mb-3">
              Section 08 · Compare Destinations
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#001F5C] leading-tight mb-3">
              UK vs Other Destinations
            </h2>
            <p className="text-base text-slate-600 leading-relaxed">
              Not sure if UK is the right call? Here’s how it stacks up against the alternatives —
              with our full guides one click away.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {comparisons.map((c) => (
              <div key={c.headline} className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#C5A028] hover:shadow-lg transition-all">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-[#C5A028]/15 flex items-center justify-center">
                    <Users className="w-4 h-4 text-[#C5A028]" />
                  </div>
                  <h3 className="text-lg font-bold font-syne text-slate-900">{c.headline}</h3>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed mb-4">{c.insight}</p>
                <Link
                  to={c.link}
                  className="inline-flex items-center gap-1.5 text-[#003087] font-semibold text-xs hover:gap-2 transition-all"
                >
                  {c.cta} <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Image break — library */}
      <div className="relative h-56 md:h-72 overflow-hidden">
        <img
          src={CAMPUS_IMG_LIBRARY}
          alt="Historic British university library reading room with stained glass windows"
          loading="lazy"
          width={1600}
          height={500}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#001F5C]/80 to-transparent" />
      </div>

      {/* ============== SMART PROFILE-EVAL CTA ============== */}
      <SmartProfileEvalCTA country="the UK" country_slug="uk-hub" testId="uk-hub-smart-cta" />

      {/* ============== FAQ ============== */}
      <section className="py-16 lg:py-24 bg-white" data-testid="uk-faq">
        <div className="max-w-4xl mx-auto px-6">
          <div className="mb-10 text-center">
            <span className="inline-block text-[11px] font-bold tracking-[0.22em] text-[#C5A028] uppercase mb-3">
              Section 09 · FAQ
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#001F5C] leading-tight mb-3">
              Frequently Asked Questions
            </h2>
            <p className="text-base text-slate-600">
              The 15 most-asked questions by Indian students applying to UK universities in 2026.
            </p>
          </div>

          <Accordion type="single" collapsible className="rounded-2xl bg-slate-50 border border-slate-200 px-6 divide-y divide-slate-200">
            {faqs.map((f, i) => (
              <AccordionItem
                key={f.q}
                value={`faq-${i}`}
                className="border-b-0"
                data-testid={`uk-faq-item-${i}`}
              >
                <AccordionTrigger className="text-left text-base font-semibold text-slate-900 hover:no-underline hover:text-[#003087] py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-slate-700 leading-relaxed pb-5 pt-0">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ============== FINAL CTA / SERVICES ============== */}
      <section
        className="relative py-20 overflow-hidden text-white"
        style={{ background: 'linear-gradient(135deg, #001F5C 0%, #003087 60%, #020617 100%)' }}
        data-testid="uk-final-cta"
      >
        <div className="absolute top-0 right-[-80px] w-[420px] h-[420px] bg-[#C5A028]/20 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute bottom-0 left-[-80px] w-80 h-80 bg-[#003087] rounded-full blur-[120px] pointer-events-none" />

        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-6 text-[10.5px] font-bold tracking-[0.22em] text-[#FACC15] uppercase">
            <Star className="w-3.5 h-3.5" />
            Ready to Apply?
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-syne mb-4 leading-tight">
            Ready to start your UK journey?
          </h2>
          <p className="text-base lg:text-lg text-slate-300 mb-8 max-w-2xl mx-auto leading-relaxed">
            Talk to a senior UK counsellor today. From university shortlisting to landing in
            London, Zafco is with you every step of the way.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-10">
            <a
              href="https://wa.me/919990533327?text=Hi%20Zafco%2C%20I%27d%20like%20to%20discuss%20studying%20in%20the%20UK."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5"
              data-testid="uk-final-whatsapp"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp: +91-9990533327
            </a>
            <a
              href="mailto:studyabroad@zafco.in"
              className="inline-flex items-center justify-center gap-2 border-2 border-white/40 hover:border-white text-white font-semibold text-sm px-7 py-3.5 rounded-full transition-all bg-white/5"
              data-testid="uk-final-email"
            >
              <Mail className="w-4 h-4" />
              studyabroad@zafco.in
            </a>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-left max-w-3xl mx-auto">
            {[
              { t: 'University Selection', icon: Building2 },
              { t: 'SOP & LOR Guidance', icon: BookOpen },
              { t: 'Visa Filing', icon: Shield },
              { t: 'Pre-Departure', icon: Briefcase },
            ].map((s) => (
              <div key={s.t} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
                <s.icon className="w-5 h-5 text-[#C5A028] mb-2" />
                <div className="text-sm font-bold text-white">{s.t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== STICKY MOBILE CTA ============== */}
      <div
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[#001F5C]/95 backdrop-blur-md border-t border-[#C5A028]/30 px-3 py-2.5 flex gap-2"
        data-testid="uk-sticky-mobile-cta"
      >
        <a
          href="https://wa.me/919990533327"
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#25D366] text-slate-900 text-xs font-bold px-3 py-2.5 rounded-full"
        >
          <MessageCircle className="w-3.5 h-3.5" />
          WhatsApp Us
        </a>
        <Link
          to="/study-abroad/book-counselling/?source=uk-sticky"
          className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#C5A028] text-black text-xs font-bold px-3 py-2.5 rounded-full"
        >
          Free Counselling
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
};

function getFlagEmoji(countryCode) {
  const codePoints = countryCode.toUpperCase().split('').map((c) => 127397 + c.charCodeAt());
  return String.fromCodePoint(...codePoints);
}

export default UKHubPage;
